import { Address, IsoDate } from '@jessie/commons';
import { compareAsc, format, isSameMonth, parseISO } from 'date-fns';
import Big from 'big.js';
import { Alignment, Content, ContentTable, TableCell } from 'pdfmake/interfaces';
import { InvoiceEntity, InvoicePosition } from '@jessie/commons';
import { formatIBAN } from 'swissqrbill/utils';

export function cm(cm: number) {
  return cm * 2.835 * 10;
}

export function humanDate(date: IsoDate, format: Intl.DateTimeFormat) {
  return format.format(parseISO(date));
}

export const DATE_FORMAT_LONG = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});
export const DATE_FORMAT_NUMERIC = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
export const DATE_FORMAT_MONTH = new Intl.DateTimeFormat('de-DE', {
  month: 'long',
  year: 'numeric',
});

export function formatNumber(
  amount: Big | number,
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = 2
) {
  return new Intl.NumberFormat('de-CH', {
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: maximumFractionDigits,
  }).format(Big(amount).toNumber());
}

export function addressBlock(name: string, address: Address): Content {
  return {
    stack: [
      name,
      address.addressLine1,
      address.addressLine2,
      { text: [address.zipCode, ' ', address.city] },
    ],
  };
}

export function invoiceFooter(invoice: InvoiceEntity) {
  return {
    fontSize: 9,
    alignment: 'center' as Alignment,
    stack: [
      {
        text: [
          invoice.sender.name,
          ' ',
          invoice.sender.address.zipCode,
          ' ',
          invoice.sender.address.city,
          ...(invoice.sender.vatUid ? [' • ', invoice.sender.vatUid, ' MWST'] : []),
        ],
      },
      {
        text: [
          invoice.bankDetail.name,
          ...(invoice.bankDetail.bic ? [' • BIC ', invoice.bankDetail.bic] : []),
          ' • IBAN ',
          formatIBAN(invoice.bankDetail.iban),
        ],
      },
    ],
  };
}

export function invoiceInformationBlock(invoice: InvoiceEntity) {
  return {
    table: {
      body: [
        [
          { text: 'Rechnung', alignment: 'left', marginTop: 1, lineHeight: 1, style: 'small' },
          {
            text: ['#', invoice.invoiceNumber],
            alignment: 'left',
            margin: 0,
            lineHeight: 1,
            bold: true,
          },
        ],
        [
          { text: 'Projekt', alignment: 'left', marginTop: 1, lineHeight: 1, style: 'small' },
          {
            text: invoice.project.displayName,
            alignment: 'left',
            margin: 0,
            lineHeight: 1,
            bold: true,
          },
        ],
        ...(invoice.deliveryPeriod.from && invoice.deliveryPeriod.to
          ? [
              [
                {
                  text: 'Lieferzeitraum',
                  alignment: 'left',
                  marginTop: 1,
                  lineHeight: 1,
                  style: 'small',
                },
                {
                  text: [
                    ...getDeliveryPeriod(invoice.deliveryPeriod.from, invoice.deliveryPeriod.to),
                  ],
                  bold: true,
                  alignment: 'left',
                  margin: 0,
                  lineHeight: 1,
                },
              ],
            ]
          : []),
      ],
    },
    layout: 'noBorders',
  };
}

export function bankInformationBlock(invoice: InvoiceEntity) {
  return {
    table: {
      body: [
        [
          { text: 'Bank', alignment: 'left', marginTop: 1, lineHeight: 1, style: 'small' },
          {
            text: invoice.bankDetail.name,
            alignment: 'left',
            margin: 0,
            lineHeight: 1,
            bold: true,
          },
        ],
        [
          { text: 'IBAN', alignment: 'left', marginTop: 1, lineHeight: 1, style: 'small' },
          {
            text: formatIBAN(invoice.bankDetail.iban),
            alignment: 'left',
            margin: 0,
            lineHeight: 1,
            bold: true,
          },
        ],
      ],
    },
    layout: 'noBorders',
  };
}

function getPositionUnit(unit: 'hours' | 'piece' | 'fixedPrice' | 'none') {
  switch (unit) {
    case 'hours':
      return 'Stunden';
    case 'fixedPrice':
      return 'Fixpreis';
    case 'none':
      return '';
    case 'piece':
      return 'Stück';
  }
}

export function getPositionTable(invoice: InvoiceEntity) {
  return {
    table: {
      widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'], // Adjust the widths as necessary
      heights: function (i: number) {
        const positionCount = invoice.positions.positionsSort.length;
        return i > 0 && i < positionCount ? cm(1.1) : 'auto';
      },
      body: [
        // Header row
        [
          { text: 'Beschreibung', style: 'small' },
          { text: 'MwSt.', alignment: 'right', style: 'small' },
          { text: 'Menge', style: 'small' },
          { text: 'Einheit', style: 'small' },
          { text: 'Preis/Einheit', alignment: 'right', style: 'small' },
          { text: 'Summe', alignment: 'right', style: 'small' },
        ],
        // First item row
        ...Object.values(invoice.positions.positions).map((position) => [
          { text: position.displayName, bold: true, marginTop: 8 },
          {
            text: [formatNumber(new Big(position.vatRate).mul(100), 1, 1), '%'],
            marginTop: 8,
            alignment: 'right',
          },
          {
            text: position.quantity != null ? formatNumber(new Big(position.quantity)) : '',
            alignment: 'right',
            marginTop: 8,
          },
          { text: getPositionUnit(position.unit), marginTop: 8 },
          {
            text: position.unitPrice != null ? formatNumber(new Big(position.unitPrice)) : '',
            alignment: 'right',
            marginTop: 8,
          },
          {
            text: formatNumber(new Big(position.totalPrice ?? 0)),
            alignment: 'right',
            marginTop: 8,
          },
        ]),

        // Empty row for spacing
        [{ text: ' ', colSpan: 6 }, {}, {}, {}, {}, {}],
        // Subtotal row
        [
          { text: '', colSpan: 4 },
          {},
          {},
          {},
          { text: 'Zwischentotal', alignment: 'right' },
          {
            text: formatNumber(new Big(invoice.invoiceTotals.netAmount ?? 0)),
            alignment: 'right',
          },
        ],
        // Tax row
        ...invoice.invoiceTotals.vat.map((vat) => [
          { text: '', colSpan: 4 },
          {},
          {},
          {},
          { text: [formatNumber(new Big(vat.rate).mul(100), 1, 1), '% MwSt'], alignment: 'right' },
          { text: formatNumber(new Big(vat.amount ?? 0)), alignment: 'right' },
        ]),
        // Total row
        [
          { text: '', colSpan: 4 },
          {},
          {},
          {},
          { text: 'Total CHF', alignment: 'right', bold: true, marginTop: 8 },
          {
            text: formatNumber(new Big(invoice.invoiceTotals.totalAmount ?? 0)),
            alignment: 'right',
            bold: true,
            marginTop: 8,
          },
        ],
      ],
    },
    layout: {
      hLineWidth: function (i: number, node: unknown) {
        return i === 1 ? 0.5 : 0; // Thin line after the header row
      },
      hLineColor: function (i: number, node: unknown) {
        return 'gray';
      },
      vLineWidth: function (i: number, node: unknown) {
        return 0;
      },
    },
  };
}

function formatHours(minutes: number) {
  return formatNumber(new Big(minutes).div(60));
}

function workReportSection(position: InvoicePosition): Content {
  const works = [...position.works].sort((a, b) => compareAsc(parseISO(a.date), parseISO(b.date)));
  const totalMinutes = works.reduce((acc, work) => acc + work.workMinutes, 0);
  const body: TableCell[][] = [
    [{ text: position.displayName, bold: true, colSpan: 4, marginBottom: 2 }, {}, {}, {}],
    [
      { text: 'Datum', style: 'small' },
      { text: 'Mitarbeiter', style: 'small' },
      { text: 'Beschreibung', style: 'small' },
      { text: 'Std.', alignment: 'right', style: 'small' },
    ],
    ...works.map((work): TableCell[] => [
      { text: format(parseISO(work.date), 'dd.MM.yyyy'), style: 'small' },
      { text: work.orgaUser.displayName, style: 'small' },
      { text: work.description ?? '', style: 'small' },
      { text: formatHours(work.workMinutes), alignment: 'right', style: 'small' },
    ]),
    [
      { text: '', colSpan: 2 },
      {},
      { text: 'Subtotal', alignment: 'right', bold: true, style: 'small' },
      { text: formatHours(totalMinutes), alignment: 'right', bold: true, style: 'small' },
    ],
  ];
  return {
    marginTop: cm(0.8),
    table: {
      headerRows: 2,
      widths: ['auto', 'auto', '*', 'auto'],
      body,
    },
    layout: {
      hLineWidth: function (i: number, node: ContentTable) {
        return i === 2 || i === node.table.body.length - 1 ? 0.5 : 0;
      },
      hLineColor: function () {
        return 'gray';
      },
      vLineWidth: function () {
        return 0;
      },
    },
  };
}

export function getWorkReportContent(invoice: InvoiceEntity): Content[] {
  if (!invoice.includeWorkReport) {
    return [];
  }
  const effortPositions = invoice.positions.positionsSort
    .map((positionId) => invoice.positions.positions[positionId])
    .filter((position): position is InvoicePosition => !!position)
    .filter((position) => position.unit === 'hours' && position.works.length > 0);

  if (effortPositions.length === 0) {
    return [];
  }

  const totalMinutes = effortPositions.reduce(
    (acc, position) => acc + position.works.reduce((sum, work) => sum + work.workMinutes, 0),
    0
  );

  return [
    {
      text: ['Anhang: Stundenrapport — Rechnung Nr. ', invoice.invoiceNumber],
      bold: true,
      fontSize: 13,
      pageBreak: 'before',
    },
    ...effortPositions.map((position) => workReportSection(position)),
    {
      marginTop: cm(0.5),
      columns: [
        { width: '*', text: '' },
        {
          width: 'auto',
          text: ['Total ', formatHours(totalMinutes), ' Stunden'],
          bold: true,
        },
      ],
    },
  ];
}

function getDeliveryPeriod(from: IsoDate, to: IsoDate) {
  if (isSameMonth(parseISO(from), parseISO(to))) {
    return humanDate(from, DATE_FORMAT_MONTH);
  } else {
    return [humanDate(from, DATE_FORMAT_NUMERIC), ' - ', humanDate(to, DATE_FORMAT_NUMERIC)];
  }
}
